import { useEffect } from 'react';

/**
 * Logo splash — mirrors script-home-v4.js timing (min 1.4s, max 4.5s).
 */
export function useLogoSplash(splashRef, logoRef) {
  useEffect(() => {
    const splash = splashRef.current;
    if (!splash) {
      document.documentElement.classList.remove('v4-splash-pending');
      return undefined;
    }

    const logo = logoRef.current;
    const minMs = 1400;
    const maxMs = 4500;
    const started = performance.now();
    let logoReady = false;
    let pageReady = document.readyState === 'complete';
    let finished = false;
    let minTimer;
    let maxTimer;

    const finish = () => {
      if (finished) return;
      finished = true;
      splash.classList.add('is-done');
      splash.setAttribute('aria-busy', 'false');
      document.documentElement.classList.remove('v4-splash-pending');
      window.setTimeout(() => {
        if (splash.parentNode) splash.remove();
      }, 700);
    };

    const tryFinish = () => {
      if (!logoReady || !pageReady) return;
      const elapsed = performance.now() - started;
      const wait = Math.max(0, minMs - elapsed);
      minTimer = window.setTimeout(finish, wait);
    };

    const markLogoReady = () => {
      logoReady = true;
      tryFinish();
    };

    if (logo) {
      if (logo.complete && logo.naturalWidth > 0) {
        markLogoReady();
      } else {
        logo.addEventListener('load', markLogoReady);
        logo.addEventListener('error', markLogoReady);
      }
    } else {
      markLogoReady();
    }

    const onLoad = () => {
      pageReady = true;
      tryFinish();
    };

    if (pageReady) {
      tryFinish();
    } else {
      window.addEventListener('load', onLoad);
    }

    maxTimer = window.setTimeout(finish, maxMs);

    return () => {
      window.clearTimeout(minTimer);
      window.clearTimeout(maxTimer);
      window.removeEventListener('load', onLoad);
      if (logo) {
        logo.removeEventListener('load', markLogoReady);
        logo.removeEventListener('error', markLogoReady);
      }
    };
  }, [splashRef, logoRef]);
}
